import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FileText, Sheet, Table, X, AlertTriangle, Eye, Download, RefreshCw, Search, ChevronDown, Check } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useApi } from '../../hooks/useApi';
import { FormRadioGroup } from '../forms/FormRadioGroup';

// Searchable Select Component for large lists
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  loading = false,
  displayKey = "fullName",
  valueKey = "_id"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayCount, setDisplayCount] = useState(50);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option => {
      const name = (option[displayKey] || option.email || '').toLowerCase();
      const email = (option.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [options, searchQuery, displayKey]);

  // Get visible options (paginated)
  const visibleOptions = filteredOptions.slice(0, displayCount);
  const hasMore = filteredOptions.length > displayCount;

  // Get selected option display text
  const selectedOption = options.find(opt => opt[valueKey] === value);
  const displayText = selectedOption ? (selectedOption[displayKey] || selectedOption.email) : '';

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchQuery('');
    setDisplayCount(50);
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    setDisplayCount(prev => prev + 50);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Value Button */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087] flex items-center justify-between text-left"
      >
        <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
          {displayText || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-slate-900">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDisplayCount(50);
                }}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-[#003087]"
              />
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {filteredOptions.length} client{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">Loading...</div>
            ) : visibleOptions.length > 0 ? (
              <>
                {visibleOptions.map(option => (
                  <button
                    key={option[valueKey]}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-between ${
                      value === option[valueKey] ? 'bg-blue-50 dark:bg-blue-900/20 text-[#003087] dark:text-blue-400' : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    <span className="truncate">{option[displayKey] || option.email}</span>
                    {value === option[valueKey] && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                ))}
                {hasMore && (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    className="w-full px-3 py-2 text-center text-sm text-[#003087] dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800 font-medium"
                  >
                    Load more ({filteredOptions.length - displayCount} remaining)
                  </button>
                )}
              </>
            ) : (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">No clients found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const COLORS = {
  primary: "#003087",
  secondary: "#FFD100",
  accent: "#DC3545"
};

const Alert = ({ message, type = 'error', onClose }) => (
  <div className={`rounded-lg p-3 mb-4 flex items-start justify-between ${type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800' : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800'
    }`}>
    <div className="flex items-start">
      <AlertTriangle className="h-4 w-4 mr-2 mt-0.5" />
      <p className="text-sm font-medium">{message}</p>
    </div>
    <button onClick={onClose} className="ml-4 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const LoadingSpinner = () => (
  <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#003087] border-t-transparent" />
);

const formatOptions = [
  { id: 'pdf', label: 'PDF', icon: FileText },
  { id: 'csv', label: 'CSV', icon: Table },
  { id: 'json', label: 'JSON', icon: Sheet }
];

const ReportsSection = ({ role = 'admin' }) => {
  const { get, post } = useApi();
  const [viewMode, setViewMode] = useState('generate');
  const [reportType, setReportType] = useState('institutional');
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCounselors, setActiveCounselors] = useState([]);
  const [inactiveCounselors, setInactiveCounselors] = useState([]);
  const [counselorType,setCounselorType]= useState(null);
  const [clients, setClients] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        if (reportType === 'counselor') {
          const activeResponse = await get('/users/counselors');
          const inactiveResponse = await get('/users/counselors?status=inactive');
          setActiveCounselors(activeResponse.data.data || []);
          setInactiveCounselors(inactiveResponse.data.data || []);
        }
        if (reportType === 'client') {
          // Fetch clients from sessions to ensure consistency with what HOD sees in session list
          const response = await get('/sessions/clients');
          // Map the session client data to the format expected by the dropdown
          const formattedClients = (response.data.data || []).map(group => ({
            _id: group.clientId,
            fullName: group.clientName,
            email: group.clientInfo?.email // Optional if available
          }));
          setClients(formattedClients);
        }
      } catch (error) {
        setError('Failed to load users. Please try again.');
      } finally {
        setLoadingUsers(false);
      }
    };

    setSelectedCounselor('');
    setSelectedClient('');

    if (['counselor', 'client'].includes(reportType)) {
      fetchUsers();
    }
  }, [reportType, get]);

  useEffect(() => {
    if (showPdfViewer && pdfUrl) {
      const adobeDCView = new window.AdobeDC.View({
        clientId: process.env.REACT_APP_ADOBE_CLIENT_ID,
        divId: "adobe-dc-view"
      });
      adobeDCView.previewFile({
        content: { location: { url: pdfUrl } },
        metaData: { fileName: "Report.pdf" }
      });
    }
  }, [showPdfViewer, pdfUrl]);

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const [startDate, endDate] = dateRange;

      if (!startDate || !endDate) throw new Error('Please select a date range');
      if (endDate < startDate) throw new Error('End date must be after start date');
      if (reportType === 'counselor' && !selectedCounselor) throw new Error('Please select a counselor');
      if (reportType === 'client' && !selectedClient) throw new Error('Please select a client');

      const response = await post('/dashboard/reports', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        format: selectedFormat,
        reportType,
        ...(reportType === 'counselor' && { counselorId: selectedCounselor }),
        ...(reportType === 'client' && { clientId: selectedClient })
      }, {
        responseType: selectedFormat === 'pdf' ? 'blob' : 'text',
        headers: {
          'Accept': selectedFormat === 'pdf'
            ? 'application/pdf'
            : selectedFormat === 'csv'
              ? 'text/csv'
              : 'application/json'
        }
      });

      if (selectedFormat === 'pdf') {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPdfViewer(true);
      } else {
        const blob = new Blob(
          [response.data],
          { type: selectedFormat === 'csv' ? 'text/csv' : 'application/json' }
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        link.download = `usiu-${reportType}-report-${timestamp}.${selectedFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
      <div className="border-b border-gray-100 dark:border-gray-800 p-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <FileText className="w-5 h-5 mr-2" style={{ color: COLORS.primary }} />
            Generate Reports
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('generate')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'generate'
                  ? 'bg-[#003087] text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
            >
              New Report
            </button>
            <button
              onClick={() => setViewMode('view')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'view'
                  ? 'bg-[#003087] text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
            >
              History
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {error && <Alert message={error} onClose={() => setError(null)} />}

        {viewMode === 'generate' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                >
                  {role === 'admin' && <option value="institutional">Institutional Report</option>}
                  <option value="counselor">Counselor Performance</option>
                  <option value="client">Client Progress</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range</label>
                <div className="grid grid-cols-2 gap-3">
                  <DatePicker
                    selected={dateRange[0]}
                    onChange={(date) => setDateRange([date, dateRange[1]])}
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm"
                    placeholderText="Start date"
                    maxDate={new Date()}
                  />
                  <DatePicker
                    selected={dateRange[1]}
                    onChange={(date) => setDateRange([dateRange[0], date])}
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm"
                    placeholderText="End date"
                    minDate={dateRange[0]}
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </div>

            {reportType === 'counselor' && (
              <>
              <FormRadioGroup
                label="Counselor Status"
                name="counselorStatus"
                options={[
                  { value: 'active', label: 'Active Counselors' },
                  { value: 'inactive', label: 'Inactive Counselors' },
                ]}
                value={counselorType}
                onChange={(e) => setCounselorType(e.target.value)}
                inline
                className="pb-2 pt-1.5"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Counselor</label>
                {loadingUsers ? (
                  <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <LoadingSpinner />
                    <span className="text-sm">Loading counselors...</span>
                  </div>
                ) : !counselorType? (
                  <select
                    className="w-full cursor-not-allowed border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                    value={selectedCounselor}
                    onChange={(e) => setSelectedCounselor(e.target.value)}
                    disabled
                  >
                    <option value="">Select a counselor status first</option>
                  </select>
                ) :counselorType === 'active' && activeCounselors.length > 0 ? (
                  <select
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                    value={selectedCounselor}
                    onChange={(e) => setSelectedCounselor(e.target.value)}
                  >
                    <option value="">Select a counselor</option>
                    {activeCounselors.map(counselor => (
                      <option key={counselor._id} value={counselor._id}>
                        {counselor.fullName || counselor.email}
                      </option>
                    ))}
                  </select>
                ) : counselorType === 'inactive' && inactiveCounselors.length > 0 ? (
                  <select
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-slate-900 dark:text-white rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                    value={selectedCounselor}
                    onChange={(e) => setSelectedCounselor(e.target.value)}
                  >
                    <option value="">Select a counselor</option>
                    {inactiveCounselors.map(counselor => (
                      <option key={counselor._id} value={counselor._id}>
                        {counselor.fullName || counselor.email}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-red-500">No counselors available</p>
                )}
              </div>
              </>
            )}

            {reportType === 'client' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Client</label>
                {loadingUsers ? (
                  <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <LoadingSpinner />
                    <span className="text-sm">Loading clients...</span>
                  </div>
                ) : clients.length > 0 ? (
                  <SearchableSelect
                    options={clients}
                    value={selectedClient}
                    onChange={setSelectedClient}
                    placeholder="Select a client"
                    searchPlaceholder="Search by name or email..."
                    loading={loadingUsers}
                    displayKey="fullName"
                    valueKey="_id"
                  />
                ) : (
                  <p className="text-sm text-red-500">No clients available</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Format</label>
              <div className="flex gap-3">
                {formatOptions.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setSelectedFormat(id)}
                    className={`flex items-center px-3 py-1.5 rounded-md transition-all ${selectedFormat === id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-[#003087] dark:text-blue-400 border border-[#003087] dark:border-blue-700'
                        : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <span className="text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              {selectedFormat === 'pdf' && pdfUrl && (
                <button
                  onClick={() => setShowPdfViewer(true)}
                  className="flex items-center px-3 py-1.5 mr-3 text-[#003087] hover:bg-blue-50 rounded-md transition-colors"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  <span className="text-sm">Preview</span>
                </button>
              )}
              <button
                onClick={handleGenerateReport}
                disabled={loading}
                className="flex items-center px-4 py-1.5 bg-[#003087] text-white rounded-md hover:bg-[#002670] transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Generate Report
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {generatedReports.map(report => (
              <div key={report.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-[#003087]" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">{report.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Generated on {report.date}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-[#003087] dark:hover:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-[#003087] dark:hover:text-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showPdfViewer && pdfUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-900 w-4/5 h-4/5 rounded-lg flex flex-col border border-gray-200 dark:border-gray-800">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Report Preview</h3>
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 p-4">
                <div id="adobe-dc-view" className="h-full w-full"></div>
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-4">
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = `usiu-report-${new Date().toISOString().split('T')[0]}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="px-4 py-2 bg-[#003087] text-white rounded-lg hover:bg-[#002670]"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsSection;